import { useTranslation } from 'react-i18next';
import { useListAgentChatThreads } from '@/entities/project/api/generated/api';
import type { ProjectAgentChatThreadSchema } from '@/entities/project/api/generated/model';
import { ChatHistoryItem } from '@/features/ai-chat/ui/ChatHistoryItem';
import { Separator, Text } from '@/shared/ui';
import { CHAT_HISTORY_PAGE_SIZE } from '@/widgets/right-side-menu/lib/chat-history-constants';
import type { AIContextMenu } from '@/widgets/right-side-menu/lib/types';
import { useThreadActions } from '../thread-actions/useThreadActions';

type Props = {
  projectId: string;
  setAiContextMenu: (context: AIContextMenu) => void;
};

export const ChatHistoryList = ({ projectId, setAiContextMenu }: Props) => {
  const { t } = useTranslation();

  // 履歴一覧取得
  const { data, isLoading } = useListAgentChatThreads(projectId, {
    page_size: CHAT_HISTORY_PAGE_SIZE,
  });

  const threads = data?.items ?? [];
  const favorites = threads.filter((thread) => thread.is_favorite);
  const recentThreads = threads.filter((thread) => !thread.is_favorite);

  const { openRename, openDelete, toggleFavorite, dialogs } = useThreadActions({ projectId });

  // スレッド選択
  const handleSelectThread = (threadId: string) => {
    setAiContextMenu({ route: 'projectDetail', mode: 'agent_chat', threadId });
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-1 py-1">
        <div className="flex h-9 items-center px-3">
          <div className="h-4 w-32 animate-pulse rounded bg-surface-secondary" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col py-1">
      {/* スクロール可能なリスト領域 */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {threads.length === 0 ? (
          <Text variant="description" className="px-3 py-4 text-center">
            {t('ai.chat-history.empty')}
          </Text>
        ) : (
          <>
            {/* 固定セクション */}
            {favorites.length > 0 && (
              <div className="flex flex-col">
                <Text
                  variant="description"
                  className="sticky top-0 z-10 mt-2 mb-1 bg-surface-base px-3 py-1"
                >
                  {t('ai.common.section-favorites')}
                </Text>
                {favorites.map((thread: ProjectAgentChatThreadSchema) => (
                  <ChatHistoryItem
                    key={thread.id}
                    title={thread.title}
                    isFavorite
                    onClick={() => handleSelectThread(thread.id)}
                    onRename={() => openRename(thread)}
                    onToggleFavorite={() => toggleFavorite(thread)}
                    onDelete={() => openDelete(thread)}
                  />
                ))}
              </div>
            )}

            {/* 最近セクション */}
            {recentThreads.length > 0 && (
              <>
                <Separator className="my-1" />
                <div className="flex flex-col">
                  <Text
                    variant="description"
                    className="sticky top-0 z-10 mt-2 mb-1 bg-surface-base px-3 py-1"
                  >
                    {t('ai.common.section-recent')}
                  </Text>
                  {recentThreads.map((thread: ProjectAgentChatThreadSchema) => (
                    <ChatHistoryItem
                      key={thread.id}
                      title={thread.title}
                      isFavorite={false}
                      onClick={() => handleSelectThread(thread.id)}
                      onRename={() => openRename(thread)}
                      onToggleFavorite={() => toggleFavorite(thread)}
                      onDelete={() => openDelete(thread)}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>

      {dialogs}
    </div>
  );
};
